import { smartNotificationService } from './intelligentScheduling/SmartNotificationService';
import { personaService } from './personaService';

export class AutoNotificationTrigger {
  private hasTriggered = false;
  
  async initialize() {
    // Ensure persona data is loaded first
    personaService.initializeIfNeeded();
    
    // Trigger an initial notification after app loads
    setTimeout(() => {
      this.checkAndTriggerNotification();
    }, 3000); // 3 seconds after app loads
  }
  
  private async checkAndTriggerNotification() {
    if (this.hasTriggered) return;
    
    try {
      console.log('🚀 Checking for immediate notifications...');
      
      const nextNotification = await smartNotificationService.getNextScheduledNotification();
      
      if (nextNotification) {
        const listId = nextNotification.scheduleInfo.listId;
        
        // Validate the list exists before generating notification
        const { listsService } = await import('./listsService');
        const list = listsService.getListById(listId);
        
        if (!list) {
          console.warn('⚠️ Skipping notification: list not found:', listId);
          return;
        }
        
        console.log('📱 Found next notification for valid list:', {
          listId: listId,
          listTitle: nextNotification.scheduleInfo.listTitle,
          nextItem: nextNotification.scheduleInfo.nextItem?.content,
          confidence: nextNotification.scheduleInfo.confidence
        });
        
        // Use a consistent schedule ID format that can be resolved
        const scheduleId = `auto_${listId}_${Date.now()}`;
        
        await smartNotificationService.sendScheduleNotification(
          scheduleId,
          listId,
          nextNotification.notification.title || `⏰ Continue: ${nextNotification.scheduleInfo.listTitle}`,
          nextNotification.notification.message || `Next: ${nextNotification.scheduleInfo.nextItem?.content}`,
          'schedule_reminder',
          {
            priority: nextNotification.notification.priority as 'low' | 'medium' | 'high' | 'critical' || 'high',
            confidence: nextNotification.scheduleInfo.confidence || 0.9,
            contextFactors: [
              'List is in progress',
              'High priority list', 
              'Daily routine pending',
              `Scheduled for ${new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()}`,
              'Next logical task in sequence',
              'auto_triggered'
            ]
          }
        );
        
        this.hasTriggered = true;
        console.log('✅ Auto-triggered notification sent successfully');
      } else {
        console.log('ℹ️ No immediate notifications due');
      }
    } catch (error) {
      console.error('❌ Failed to check and trigger notification:', error);
    }
  }
  
  // Method to manually trigger (for testing)
  async testTrigger() {
    this.hasTriggered = false;
    await this.checkAndTriggerNotification();
  }
}

export const autoNotificationTrigger = new AutoNotificationTrigger();

// Add test function to window for debugging
if (typeof window !== 'undefined') {
  (window as any).testAutoNotification = () => autoNotificationTrigger.testTrigger();
  console.log('💡 Test auto notification available: Run testAutoNotification() in console');
}